/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IS_REGION_US, NumberTypes, State } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { SelectionModel, emptySelectionModel, selectionModelAfterRowRemoved } from '@gh/core-data';
import { FocusedCell, GridLayout } from '@gh/core-ui-grid';
import { DialogService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { filter$, map$, switchMap$, take$, takeUntil$ } from '@gh/rx/operators';
import { CellDoubleClickedEvent } from 'ag-grid-community';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest } from 'rxjs';

import {
  getJeCostCenterId,
  getJeItemId,
  isJeFreeItem,
  isJeFreeOrComponentItem,
  JeItem,
  JeItemType,
} from '../../../resources/job-estimate-details.model';
import { JeCommandService } from '../je-command.service';
import {
  ensureJeSalesTaxDivider,
  JeDetailsState,
  JeFocusedTable,
  queryJeCostCenterItems,
  queryJeEditable,
  queryJeFocusedCell,
  queryJeItem,
  queryJeItemLayout,
  queryJeSalesTaxRates,
  queryJeSelectedCostCenter,
  queryJeSelectedCostCenterId,
  queryJeSelectedItemId,
} from '../je-details.state';
import { JeDetailsCcItemGridDefinition } from './je-details-cc-item-grid-definition.service';
import { reduce } from 'lodash';
@Component({
  standalone: false,
  selector: 'gh-je-details-cc-item-list',
  templateUrl: 'je-details-cc-item-list.component.html',
  styleUrls: ['je-details-cc-item-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [JeDetailsCcItemGridDefinition],
})
export class JeDetailsCcItemListComponent implements OnInit, OnDestroy {
  total$: Observable<number>;
  items$: Observable<JeItem[]>;
  selectedItemId$: Observable<Maybe<ID>>;
  focusedCell$: Observable<Maybe<FocusedCell>>;
  str$: Observable<string>;
  editable$: Observable<boolean>;
  ccCost$: Observable<number>;
  combineCosts$: Observable<boolean>;

  isUsRegion = IS_REGION_US;

  private costCenterId: ID;
  private lastSeenCostCenterId: Maybe<ID>;
  private costCenterIdSubscription: Subscription;
  private readonly destroy$ = new Subject<void>();
  /** Emits when checkbox selection changes so `items$`-derived streams recompute (items$ alone does not emit on select). */
  private selectionModelVersion$ = new BehaviorSubject<void>(undefined);

  sourceSelectionModel = emptySelectionModel();

  constructor(private route: ActivatedRoute,
              public gridDefinition: JeDetailsCcItemGridDefinition,
              private state: State<JeDetailsState>,
              private commands: JeCommandService,
              private dialogService: DialogService) {

  }

  ngOnInit(): void {
    this.gridDefinition.bind(this.state.project(queryJeItemLayout));

    const { state } = this;
    const costCenterId$ = state.select(queryJeSelectedCostCenterId).pipe(filter$(Boolean));
    const costCenter$ = state.select(queryJeSelectedCostCenter).pipe(filter$(Boolean));

    this.items$ = costCenter$.pipe(switchMap$((cc: any) => state.select(queryJeCostCenterItems, getJeCostCenterId(cc)).pipe(
      map$(ensureJeSalesTaxDivider))));
    this.ccCost$ = costCenter$.pipe(map$((component: any) => component.cost || 0));
    this.total$ = this.items$.pipe(
      map$((items) => reduce(items, (sum, n) =>  sum + n.extension, 0) || 0));
    this.combineCosts$ = combineLatest(this.ccCost$, this.total$).pipe(
      map$(([cost, total]) => cost ===  +total.toFixed(2)));
    this.selectedItemId$ = state.select(queryJeSelectedItemId);
    this.focusedCell$ = this.state.select(queryJeFocusedCell, JeFocusedTable.ItemList);
    this.str$ = this.state.select(queryJeSalesTaxRates).pipe(
      map$((rates) => rates.map((rate) => NumberTypes.Rate.format(rate.ratePct) + '%').join('; ')));

    this.costCenterIdSubscription = costCenterId$.subscribe((id: any) => {
      this.costCenterId = id;
      if (this.lastSeenCostCenterId !== void 0 && this.lastSeenCostCenterId !== id) {
        this.clearMultiSelectForCostCenterContextChange();
      }
      this.lastSeenCostCenterId = id;
    });
    this.editable$ = this.state.select(queryJeEditable);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.costCenterIdSubscription.unsubscribe();
  }

  onAdd(): void {
    this.commands.costCenterItemAdd(this.costCenterId);
  }

  onCloneItems(items: JeItem): void {
    this.commands.costCenterItemClone(items);
  }

  onDelete(item: JeItem): void {
    const id = getJeItemId(item);
    this.commands.costCenterItemRemove(item).pipe(
      takeUntil$(this.destroy$),
    ).subscribe(() => {
      const items = this.state.project(queryJeCostCenterItems, this.costCenterId) || [];
      const next = selectionModelAfterRowRemoved(
        this.sourceSelectionModel,
        id,
        items.length === 0,
      );
      if (next !== this.sourceSelectionModel) {
        this.onItemSelectionChanged(next);
      }
    });
  }
  
  canBeDeleted(item: JeItem): boolean {
    return item.type === JeItemType.Item;
  }

  canChangeCc(item: JeItem): boolean {
    return isJeFreeItem(item);
  }

  onSelectedItemChanged(id?: ID): void {
    if (!id) {
      this.commands.itemSelect(void 0);
      return;
    }
    const item = this.state.project(queryJeItem, id);
    if (item && (item.type === JeItemType.Item || item.type === JeItemType.Component)) {
      this.commands.itemSelect(id);
    }
  }

  onFocusedCellChanged(): void {
    this.commands.resetFocusedCell();
  }

  onCellDoubleClicked({ colDef, data }: CellDoubleClickedEvent): void {
    if (this.state.project(queryJeEditable) && colDef.colId === 'variation') {
      this.commands.itemConfirmVariation(getJeItemId(data));
    }
  }

  onChangeCc(item: JeItem): void {
    this.commands.itemChangeCc(getJeItemId(item));
  }

  onOpenStr(): void {
    this.commands.openStr();
  }

  onLayoutChanged(layout: GridLayout): void {
    this.commands.layoutUpdate(layout);
  }

  filterCostCenterItem(item: JeItem): boolean {
    return isJeFreeOrComponentItem(item);
  }

  onItemSelectionChanged(model: SelectionModel): void {
    this.sourceSelectionModel = model;
    this.selectionModelVersion$.next(undefined);
  }

  private clearMultiSelectForCostCenterContextChange(): void {
    this.onItemSelectionChanged(emptySelectionModel());
  }

  getSelectedItems(): Observable<JeItem[]> {
    return combineLatest([this.items$, this.selectionModelVersion$]).pipe(
      map$(([items]) => {
        if (!items || items.length === 0) {
          return [];
        }
        const { isExcludeMode, idList } = this.sourceSelectionModel;
        if (isExcludeMode) {
          return items.filter((item) =>
            !idList.includes(getJeItemId(item)) && this.canBeDeleted(item));
        }
        return items.filter((item) =>
          idList.includes(getJeItemId(item)) && this.canBeDeleted(item));
      }));
  }

  hasSelectedItems(): Observable<boolean> {
    return this.getSelectedItems().pipe(
      map$((selectedItems) => selectedItems.length > 0));
  }

  onDeleteSelected(): void {
    this.getSelectedItems().pipe(
      take$(1),
      filter$((selectedItems) => selectedItems.length > 0),
      switchMap$((selectedItems) => {
        const count = selectedItems.length;
        const confirmConfig = count === 1
          ? yesNoConfirmation('msg_bulk_remove_single_item', { code: selectedItems[0].code })
          : yesNoConfirmation('msg_bulk_remove_n_items', { count: String(count) });
        return this.dialogService.confirmation(confirmConfig).pipe(
          filter$(yesAnswer),
          map$(() => selectedItems));
      }),
    ).subscribe((selectedItems) => {
      this.commands.removeItems(selectedItems.map((item) => getJeItemId(item)));
      this.sourceSelectionModel = emptySelectionModel();
      this.onItemSelectionChanged(this.sourceSelectionModel);
    });
  }
}
